import { useEffect, useState, useMemo } from 'react';
import { User, RotateCcw, Edit3, Trash2, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchGetAbsensiAll, fetchDeleteAbsensiRecord } from '../../../controller/dashboard/Absensi';
import ConfirmModal from '../../../components/ConfirmModal';
import FormAbsensiModal from '../../../components/FormAbsensiModal';
import { Absensi } from '../../../model/Absensi';
import { getThemeStyles, Theme } from '../../../util/style';

// Helper: ambil YYYY-MM-DD tanpa kena timezone
const parseTanggal = (tgl?: string) => {
    if (!tgl || tgl === '-') return null;
    const datePart = tgl.split('T')[0].split(' ')[0]; // "2026-07-20"
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return null;
    return {
        y,
        m: m.padStart(2, '0'),
        d: d.padStart(2, '0'),
        iso: datePart,
        ym: `${y}-${m}`,
        ymd: `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`
    };
};

const getNamaKaryawan = (absen: Absensi) => {
    const karyawan = absen.karyawan;
    if (!karyawan) return '-';
    if (typeof karyawan === 'string') return karyawan || '-';

    if ('nama_karyawan' in karyawan && typeof karyawan.nama_karyawan === 'string' && karyawan.nama_karyawan) {
        return karyawan.nama_karyawan;
    }
    if (karyawan.nama) return karyawan.nama;
    if ('username' in karyawan && typeof karyawan.username === 'string' && karyawan.username) {
        return karyawan.username;
    }
    return karyawan.id ? String(karyawan.id) : '-';
};

const formatTanggal = (tgl: string) => {
    const parsed = parseTanggal(tgl);
    if (!parsed) return tgl || '-';
    return new Date(Number(parsed.y), Number(parsed.m) - 1, Number(parsed.d))
        .toLocaleDateString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const formatJam = (jam?: string | null) => {
    if (!jam || jam === '-') return '-';
    const time = jam.split('T')[1]?.split('.')[0] || jam.split(' ')[1] || jam;
    return time.match(/^\d{2}:\d{2}/)?.[0] || time;
};

const getDurasiJam = (jamMasuk?: string | null, jamKeluar?: string | null) => {
    if (!jamMasuk || jamMasuk === '-' || !jamKeluar || jamKeluar === '-') return null;
    const waktuMasuk = new Date(jamMasuk).getTime();
    const waktuKeluar = new Date(jamKeluar).getTime();
    if (!Number.isFinite(waktuMasuk) || !Number.isFinite(waktuKeluar)) return null;
    const durasi = Math.floor((waktuKeluar - waktuMasuk) / (1000 * 60 * 60));
    return durasi > 0 ? durasi : null;
};

export default function PersonaliaDataAbsensi({ theme = 'light' }: { theme?: Theme }) {
    const navigate = useNavigate();
    const s = getThemeStyles(theme);
    const isDark = theme === 'dark';
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(1); // 1 = 1 bulan per halaman, ganti 2 kalau mau 2 bulan
    const [showFormModal, setShowFormModal] = useState(false);
    const [statusFormModal, setStatusFormModal] = useState("none")
    const [absensiData, setAbsensiData] = useState<Absensi[]>([]);
    const [selectedAbsensi, setSelectedAbsensi] = useState<Absensi | null>(null);

    const today = new Date();
    const todayYear = String(today.getFullYear());
    const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
    const todayDay = String(today.getDate()).padStart(2, '0');

    // MAU PAS DIBUKA LANGSUNG HARI INI
    const [filterYear, setFilterYear] = useState<string>(todayYear);
    const [filterMonth, setFilterMonth] = useState<string>(todayMonth);
    const [filterDay, setFilterDay] = useState<string>(todayDay);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredAkuns = useMemo(() => {
        return absensiData.filter(absen => {
            const p = parseTanggal(absen.tanggal);
            if (!p) return false;
            if (filterYear !== 'all' && p.y !== filterYear) return false;
            if (filterMonth !== 'all' && p.m !== filterMonth) return false;
            if (filterDay !== 'all' && p.d !== filterDay) return false;
            const query = searchQuery.trim().toLocaleLowerCase('id-ID');
            if (query) {
                const searchableText = [
                    getNamaKaryawan(absen), absen.tanggal, formatTanggal(absen.tanggal),
                    absen.jam_masuk, absen.jam_keluar, absen.status,
                    absen.status_coord, absen.tipe_aktivitas, absen.penyetujuan_aktivitas
                ].filter(Boolean).join(' ').toLocaleLowerCase('id-ID');
                if (!searchableText.includes(query)) return false;
            }
            return true;
        });
    }, [absensiData, filterYear, filterMonth, filterDay, searchQuery]);

    // Group by YYYY-MM biar pagination per bulan tetap rapi
    const groups = useMemo(() => {
        const groupsMap: { [key: string]: Absensi[] } = {};
        filteredAkuns.forEach(a => {
            const p = parseTanggal(a.tanggal);
            if (!p) return;
            const key = p.ym; // 2026-07
            if (!groupsMap[key]) groupsMap[key] = [];
            groupsMap[key].push(a);
        });

        const groupKeys = Object.keys(groupsMap).sort((a,b) => b.localeCompare(a)); // bulan terbaru dulu
        return groupKeys.map(k => ({
            key: k,
            rows: (groupsMap[k] || []).slice().sort((x,y)=> (y.tanggal||'').localeCompare(x.tanggal||''))
        }));
    }, [filteredAkuns]);

    const totalPages = itemsPerPage > 0 ? Math.ceil(groups.length / itemsPerPage) : 0;
    const pageNumbers = totalPages > 0 ? Array.from({ length: totalPages }, (_, i) => i + 1) : [];

    const currentGroups = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return groups.slice(start, start + itemsPerPage);
    }, [groups, currentPage, itemsPerPage]);

    const currentAkuns = useMemo(() => currentGroups.flatMap(g => g.rows), [currentGroups]);

    const initDataKaryawan = async () => { 
        setLoading(true);
        setAbsensiData([]);
        try {
            const res = await fetchGetAbsensiAll();
            if (res.status === 200) { setAbsensiData(res.data); } else { setAbsensiData([]); }
        } catch (error) {
            console.error(error);
            setAbsensiData([]);
        } finally {
            setLoading(false);
        }
    }

    const handleEditAkun = (absen: Absensi) => {
        setStatusFormModal('edit');
        setSelectedAbsensi(absen);
        setShowFormModal(true);
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<Absensi | null>(null);

    const handleRequestDelete = (absen: Absensi) => {
        setDeleteCandidate(absen);
        setShowDeleteConfirm(true);
    }

    const confirmDelete = async () => {
        if(!deleteCandidate) return;
        await fetchDeleteAbsensiRecord(deleteCandidate.id);
        setShowDeleteConfirm(false);
        setDeleteCandidate(null);
        initDataKaryawan();
    }

    useEffect(() => {
        initDataKaryawan();
    }, []);

    // Tahun yang ada di data
    const availableYears = useMemo(() => {
        const years = new Set<string>();
        absensiData.forEach(a => {
            const p = parseTanggal(a.tanggal);
            if (p) years.add(p.y);
        });
        years.add(todayYear);
        return Array.from(years).filter(Boolean).sort((a,b)=>b.localeCompare(a));
    }, [absensiData, todayYear]);

    return (
        <>
        <div className="flex h-dvh w-full flex-col overflow-hidden">
        <div className={`min-h-0 w-full min-w-0 flex-1 overflow-y-auto ${s.bg} ${s.text} p-3 sm:p-4 lg:p-6`}>
            <div className="mx-auto w-full min-w-0 max-w-screen-2xl">
            <div className="mb-6 sm:mb-8">
                <div className="flex items-start sm:items-center mb-4">
                <User className={`w-6 h-6 sm:w-8 sm:h-8 ${s.accent} mr-3 mt-1 sm:mt-0 shrink-0`} />
                <div>
                    <h1 className={`text-2xl sm:text-3xl font-bold ${s.text}`}>Absensi Karyawan</h1>
                    <p className={`text-xs sm:text-sm ${s.textSecondary}`}>Default filter: hari ini ({todayDay}-{todayMonth}-{todayYear})</p>
                </div>
                </div>
            </div>

            <div className={`${s.card} border ${s.cardBorder} rounded-xl shadow-lg p-4 sm:p-6 mb-6`}>
                <div className="flex min-w-0 flex-col gap-3">
                    <div className="grid min-w-0 grid-cols-1 gap-3">
                        <select value={filterYear} onChange={(e) => { setFilterYear(e.target.value); setCurrentPage(1); }} className={`min-h-11 w-full min-w-0 rounded-lg border px-3 py-2 ${s.card} ${s.text} ${s.cardBorder}`}>
                            <option value="all">Semua Tahun</option>
                            {availableYears.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <select value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setCurrentPage(1); }} className={`min-h-11 w-full min-w-0 rounded-lg border px-3 py-2 ${s.card} ${s.text} ${s.cardBorder}`}>
                            <option value="all">Semua Bulan</option>
                            {Array.from({length:12}, (_,i) => ({v: String(i+1).padStart(2,'0'), label: new Date(0, i).toLocaleString('id-ID', { month: 'long' })})).map(m => (
                                <option key={m.v} value={m.v}>{m.label}</option>
                            ))}
                        </select>
                        <select value={filterDay} onChange={(e) => { setFilterDay(e.target.value); setCurrentPage(1); }} className={`min-h-11 w-full min-w-0 rounded-lg border px-3 py-2 ${s.card} ${s.text} ${s.cardBorder}`}>
                            <option value="all">Semua Tanggal</option>
                            {Array.from({length:31}, (_,i) => String(i+1).padStart(2,'0')).map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid min-w-0 grid-cols-1 gap-3">
                        <button
                            onClick={initDataKaryawan}
                            disabled={loading}
                            className={`flex min-h-11 w-full items-center justify-center rounded-lg px-4 py-2 text-sm ${isDark ? 'text-[#e6edf3] bg-[#21262d] hover:bg-[#30363d]' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}>
                            <RotateCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            {loading ? "Memuat..." : "Muat Ulang"}
                        </button>
                        <label className={`flex min-h-11 w-full min-w-0 items-center gap-2 rounded-lg border px-3 py-2 ${s.card} ${s.cardBorder}`}>
                            <Search className={`h-4 w-4 shrink-0 ${s.textSecondary}`} />
                            <input
                                type="search"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                placeholder="Cari karyawan atau aktivitas..."
                                aria-label="Cari absensi"
                                className={`w-full min-w-0 bg-transparent text-sm outline-none ${s.text} placeholder:${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                            />
                        </label>
                        
                        <button
                            onClick={() => { setFilterYear(todayYear); setFilterMonth(todayMonth); setFilterDay(todayDay); setCurrentPage(1); }}
                            className={`min-h-11 w-full rounded-lg px-4 py-2 text-sm text-white ${isDark ? 'bg-[#0969da] hover:bg-[#1f6feb]' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            Hari Ini
                        </button>
                        <button
                            onClick={() => { setFilterYear('all'); setFilterMonth('all'); setFilterDay('all'); setSearchQuery(''); setCurrentPage(1); }}
                            className={`min-h-11 w-full rounded-lg border px-4 py-2 text-sm ${isDark ? 'text-[#e6edf3] bg-[#161b22] border-[#30363d] hover:bg-[#21262d]' : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'}`}>
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            <div className={`${s.card} border ${s.cardBorder} rounded-xl shadow-lg overflow-hidden`}>
                {loading ? (
                    <div className={`py-8 text-center ${s.textSecondary}`}>Sedang Loading...</div>
                ) : currentAkuns.length === 0 ? (
                    <div className={`px-4 py-8 text-center ${s.textSecondary}`}>
                        Tidak ada absensi untuk {filterDay !== 'all' ? filterDay : ''} {filterMonth !== 'all' ? filterMonth : ''} {filterYear}
                        <br/><span className="text-xs">Coba ganti filter ke Semua</span>
                    </div>
                ) : (
                    <>
                    {/* <div className="hidden min-[870px]:block overflow-x-auto">
                    <table className={`min-w-full divide-y ${isDark ? 'divide-[#21262d]' : 'divide-gray-200'}`}>
                        <thead className={isDark ? 'bg-[#1c2330]' : 'bg-gray-50'}>
                        <tr>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${s.textSecondary}`}>Karyawan</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${s.textSecondary}`}>Jam Absen</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${s.textSecondary}`}>Aktivitas</th>
                            <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${s.textSecondary}`}>Aksi</th>
                        </tr>
                        </thead>
                        <tbody className={`divide-y ${isDark ? 'divide-[#21262d]' : 'divide-gray-200'}`}>
                            <RenderTableAbsensi data={currentAkuns} onOpenEdit={handleEditAkun} onRequestDelete={handleRequestDelete} onRemoveState={initDataKaryawan} />
                        </tbody>
                    </table>
                    </div> */}
                    <div className={`divide-y ${isDark ? 'divide-[#21262d]' : 'divide-gray-200'}`}>
                        {currentAkuns.map((absen) => (
                            <article key={absen.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h2 className={`font-semibold ${s.text} break-words`}>{getNamaKaryawan(absen)}</h2>
                                        <p className={`mt-1 text-xs ${s.textSecondary}`}>{formatTanggal(absen.tanggal)}</p>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${absen.jam_keluar && absen.jam_keluar !== '-' ? (isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700')}`}>
                                        {absen.jam_keluar && absen.jam_keluar !== '-' ? (absen.status || 'Selesai') : absen.status !== '-' ? 'Berlangsung' : 'Belum Absen'}
                                    </span>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className={`text-xs ${s.textSecondary}`}>Jam Masuk</p>
                                        <p className={`font-medium ${s.text}`}>{formatJam(absen.jam_masuk)}</p>
                                    </div>
                                    <div>
                                        <p className={`text-xs ${s.textSecondary}`}>Jam Keluar</p>
                                        <p className={`font-medium ${s.text}`}>{formatJam(absen.jam_keluar)}</p>
                                    </div>
                                    {getDurasiJam(absen.jam_masuk, absen.jam_keluar) !== null && (
                                        <div className="col-span-2">
                                            <p className={`text-xs ${s.textSecondary}`}>Durasi</p>
                                            <p className={`font-medium ${s.text}`}>{absen.jam_masuk} - {absen.jam_keluar} {`(${getDurasiJam(absen.jam_masuk, absen.jam_keluar)})`} jam</p>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${absen.status === 'tepat' ? (isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700') : absen.status === 'terlambat' ? (isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700') : (isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700')}`}>{absen.status === '-' ? 'Tidak ada Status' : absen.status }</span>
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${absen.status_coord === 'in' ? (isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700')}`}>{absen.status_coord === "in" ? "Absen Di lokasi" : absen.status_coord === "out" ? "Absen di luar lokasi" : "Titik Koordinat tidak muncul"}</span>
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>{absen.tipe_aktivitas || '-'}</span>
                                    {absen.penyetujuan_aktivitas && absen.penyetujuan_aktivitas !== 'none' && (
                                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>{absen.penyetujuan_aktivitas}</span>
                                    )}
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-2">
                                    <button onClick={() => handleEditAkun(absen)} className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${isDark ? 'border-green-800 text-green-300 hover:bg-green-900/30' : 'border-green-200 text-green-700 hover:bg-green-50'}`}>
                                        <Edit3 className="h-4 w-4" /> Edit
                                    </button>
                                    <button onClick={() => handleRequestDelete(absen)} className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${isDark ? 'border-red-800 text-red-300 hover:bg-red-900/30' : 'border-red-200 text-red-700 hover:bg-red-50'}`}>
                                        <Trash2 className="h-4 w-4" /> Hapus
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                    </>
                )}
            </div>

            <div className="mt-4 sm:mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className={`text-xs sm:text-sm ${s.textSecondary}`}>
                Menampilkan <span className="font-medium">{currentAkuns.length}</span> dari{' '}
                <span className="font-medium">{filteredAkuns.length}</span> data (filter hari ini) | Total data: {absensiData.length}
                </div>
                <div className="flex items-center justify-between gap-2 sm:justify-end">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className={`px-3 py-2 text-sm border rounded-lg disabled:opacity-50 ${isDark ? 'text-[#e6edf3] border-[#30363d] hover:bg-[#21262d]' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}>Previous</button>
                <span className={`text-sm sm:hidden ${s.textSecondary}`}>{totalPages ? `${currentPage} / ${totalPages}` : '0 / 0'}</span>
                <div className="hidden sm:flex space-x-2">
                { pageNumbers.map((page) =>(
                    <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded ${currentPage === page ? 'bg-blue-600 text-white' : isDark ? 'bg-[#21262d] text-[#e6edf3]' : 'bg-gray-200 text-gray-800'}`}>{page}</button>
                ))}
                </div>
                <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => prev + 1)} className={`px-3 py-2 text-sm border rounded-lg disabled:opacity-50 ${isDark ? 'text-[#e6edf3] border-[#30363d] hover:bg-[#21262d]' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}>Next</button>
                </div>
            </div>
            </div>
        </div>
        </div>
        { showFormModal && <FormAbsensiModal absensiDetail={selectedAbsensi} setShowFormModal={setShowFormModal} onPostState={initDataKaryawan} /> }
        { showDeleteConfirm && <ConfirmModal title="Hapus Absensi" onConfirm={confirmDelete} onCancel={() => setShowDeleteConfirm(false)}>Apakah Anda yakin ingin menghapus absensi tanggal {deleteCandidate?.tanggal}?</ConfirmModal> }
        </>
    )
}
